import React from 'react';
import { WidgetId } from '../../../AccountPerformanceReporting/api/useWidgetsQuery';
import { AssetAllocationChartContent } from '../../../AccountPerformanceReporting/components/AssetAllocationChart/components/AssetAllocationChartContent/AssetAllocationChartContent';
import { StyledCard } from '../../../AccountPerformanceReporting/components/DesktopWidgets/widgets/ValueVsInvestmentCard/ValueVsInvestmentCard.style';
import { GrossOfFeeIcon } from '../../../AccountPerformanceReporting/components/GrossOfFeeIcon/GrossOfFeeIcon';
import { GroupSelectionRenderer } from '../../../AccountPerformanceReporting/components/GroupSelection/GroupSelection';
import { IncomeDetailsContent } from '../../../AccountPerformanceReporting/components/IncomeDetails/components/IncomeDetailsContent/IncomeDetailsContent';
import { LabeledList } from '../../../AccountPerformanceReporting/components/LabeledList/LabeledList';
import { MarketValueContent } from '../../../AccountPerformanceReporting/components/MarketValue/components/MarketValueContent/MarketValueContent';
import { PerformanceChartContent } from '../../../AccountPerformanceReporting/components/PerformanceChart/components/PerformanceChartContent/PerformanceChartContent';
import { PerformanceDetailsContent } from '../../../AccountPerformanceReporting/components/PerformanceDetails/components/PerformanceDetailsContent/PerformanceDetailsContent';
import { PerformanceVsBenchmarkChartContent } from '../../../AccountPerformanceReporting/components/PerformanceVsBenchmarkChart/components/PerformanceVsBenchmarkChartContent/PerformanceVsBenchmarkChartContent';
import { PeriodReturnsContent } from '../../../AccountPerformanceReporting/components/PeriodReturn/components/PeriodReturnsContent/PeriodReturnsContent';
import { ProjectedIncomeChartContent } from '../../../AccountPerformanceReporting/components/ProjectedIncomeChart/ProjectedIncomeChartContent/ProjectedIncomeChartContent';
import { ValueVsInvestmentChartContent } from '../../../AccountPerformanceReporting/components/ValueVsInvestmentChart/components/ValueVsInvestmentChartContent/ValueVsInvestmentChartContent';
import { RealizedGainLossDetailsContent } from '../../../AccountPerformanceReporting/tabs/GainLoss/Components/RealizedGainLossTab/components/RealizedGainLossDetails/components/RealizedGainLossDetailsContent/RealizedGainLossDetailsContent';
import { Totals } from '../../../AccountPerformanceReporting/tabs/GainLoss/Components/Totals/Totals';
import { UnrealizedGainLossDetailsContent } from '../../../AccountPerformanceReporting/tabs/GainLoss/Components/UnrealizedGainLossTab/components/UnrealizedGainLossDetails/components/UnrealizedGainLossDetailsContent/UnrealizedGainLossDetailsContent';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Card } from '../../../CommonComponents/OPL/Card/Card';
import { SegmentedControl } from '../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { ChartType } from '../../../CommonSelectors/CommonArea.types';
import {
  accountAssetAllocationChartContentMock,
  activitySummaryDataMock,
  assetClassAllocationChartContentMock,
  incomeDetailsMockResponse,
  marketValueChartContentMock,
  performanceChartMock,
  performanceDetailsCardMock,
  performanceSummaryMock,
  performanceVSBenchmarkMock,
  periodReturnsContentMock,
  projectedIncomeChartMockResponse,
  realizedGainsLossesMock,
  realizedGainsLossesSummaryMock,
  unrealizedGainsLossesMock,
  unrealizedGainsLossesSummaryMock,
  valueVsInvestmentCardMock,
} from '../Widgets/Widgets.meta';

interface Props {
  id: WidgetId;
  title: string;
}

export const WidgetContent = ({ id, title }: Props) => {
  if (id === 'market-value') {
    return (
      <Card p="16">
        <MarketValueContent {...marketValueChartContentMock} />
      </Card>
    );
  }

  if (id === 'return') {
    return (
      <Card p="16">
        <PeriodReturnsContent {...periodReturnsContentMock} />
      </Card>
    );
  }

  if (id === 'activity-summary') {
    return (
      <Card p="16" display="flex" flexDirection="column">
        <Text variant="cardTitle" mb="24">
          {title}
        </Text>

        <Box display="flex" flexDirection="column" flex={1}>
          <LabeledList data={activitySummaryDataMock} />
        </Box>
      </Card>
    );
  }

  if (id === 'pv-vs-nai') {
    return (
      <StyledCard>
        <Text variant="cardTitle" mb="12">
          {title}
          <GrossOfFeeIcon />
        </Text>

        <ValueVsInvestmentChartContent {...valueVsInvestmentCardMock} />
      </StyledCard>
    );
  }

  if (id === 'allocation-slot-a') {
    return (
      <Card display="flex" flexDirection="column">
        <AssetAllocationChartContent
          {...assetClassAllocationChartContentMock}
          chartType={ChartType.Donut}
          showLink={false}
        />
      </Card>
    );
  }

  if (id === 'allocation-slot-b') {
    return (
      <Card display="flex" flexDirection="column">
        <AssetAllocationChartContent
          {...accountAssetAllocationChartContentMock}
          chartType={ChartType.Donut}
          showLink={false}
        />
      </Card>
    );
  }

  if (id === 'performance-summary') {
    return (
      <Card display="flex" flexDirection="column" maxHeight={400} pt="12" pb="24">
        <Text variant="cardTitle" mb="24" px="16">
          {title}
          <GrossOfFeeIcon />
        </Text>
        <Box px="16" flex={1} css="overflow-y: auto">
          <LabeledList data={performanceSummaryMock} />
        </Box>
      </Card>
    );
  }

  if (id === 'performance-vs-benchmark') {
    return (
      <Card height={400} px="16" pt="12" pb="16">
        <Text variant="cardTitle" mb="12">
          Performance vs Benchmark
          <GrossOfFeeIcon />
        </Text>

        <Box flex={1}>
          <PerformanceVsBenchmarkChartContent
            data={performanceVSBenchmarkMock}
            isBenchmarkHidden={_ => false}
            setSelectedDate={() => {}}
            toggleBenchmarkVisibility={() => {}}
          />
        </Box>
      </Card>
    );
  }

  if (id === 'performance-details') {
    return (
      <Box>
        <GroupSelectionRenderer id={id}>
          <SegmentedControl
            my="16"
            id={`${id}-drill-down-sc`}
            values={['Account', 'Asset Category', 'Asset Class']}
            selectedIndex={0}
            onChange={() => {}}
            ariaLabel="Select group"
          />
        </GroupSelectionRenderer>

        <PerformanceDetailsContent {...performanceDetailsCardMock} />
      </Box>
    );
  }

  if (id === 'projected-income') {
    return (
      <Card p="16">
        <Text variant="cardTitle" mb="24">
          {title}
        </Text>

        <Box>
          <ProjectedIncomeChartContent data={projectedIncomeChartMockResponse} />
        </Box>
      </Card>
    );
  }

  if (id === 'income-details') {
    return (
      <Box>
        <GroupSelectionRenderer id={id}>
          <SegmentedControl
            my="16"
            id={`${id}-drill-down-sc`}
            values={['Account', 'Asset Category', 'Asset Class']}
            selectedIndex={0}
            onChange={() => {}}
            ariaLabel="Select group"
          />
        </GroupSelectionRenderer>

        <IncomeDetailsContent
          isRowExpanded={_ => false}
          searchTerm=""
          setSearchTerm={() => {}}
          setSortBy={() => {}}
          sortBy={{
            column: 'name',
            sort: 'none',
          }}
          tableData={incomeDetailsMockResponse}
          title={title}
          toggleRowExpanded={() => {}}
        />
      </Box>
    );
  }

  if (id === 'performance-chart') {
    return (
      <Box>
        <GroupSelectionRenderer id={id}>
          <SegmentedControl
            my="16"
            id={`${id}-drill-down-sc`}
            values={['Account', 'Asset Category', 'Asset Class']}
            selectedIndex={0}
            onChange={() => {}}
            ariaLabel="Select group"
          />
        </GroupSelectionRenderer>

        <Card>
          <Text variant="cardTitle" px="16" pt="12" pb="4">
            {title}
          </Text>

          <Box>
            <PerformanceChartContent {...performanceChartMock} />
          </Box>
        </Card>
      </Box>
    );
  }

  if (id === 'unrealized-gainloss-summary') {
    return <Totals gainLossTotals={unrealizedGainsLossesSummaryMock} title={title} />;
  }

  if (id === 'unrealized-gainloss-detail') {
    return (
      <UnrealizedGainLossDetailsContent
        data={unrealizedGainsLossesMock}
        title={title}
        debouncedSearchTerm=""
        setSearchTerm={() => {}}
        searchTerm=""
        setPositionMobile={() => {}}
      />
    );
  }

  if (id === 'realized-gainloss-summary') {
    return <Totals gainLossTotals={realizedGainsLossesSummaryMock} title={title} />;
  }

  if (id === 'realized-gainloss-detail') {
    return (
      <RealizedGainLossDetailsContent
        title={title}
        data={realizedGainsLossesMock}
        debouncedSearchTerm=""
        setSearchTerm={() => {}}
        searchTerm=""
        setPositionMobile={() => {}}
      />
    );
  }

  return null;
};
